import React, { useState, useEffect, useContext } from "react";
import PopoverInput from "./inner/popoverInput.jsx";
import { pinIcon as icon } from "./inner/fieldIcons.jsx";
import { PinIcon } from "../../../SubModules/Olympic.DesignFramework/react/icons.jsx";
import PickerFooter from "./inner/pickerFooter.jsx";
import { DataSourceContext, IsMobileContext } from "../context/contexts.jsx";
import { isHotelCode } from "../helpers/geography.js";
import { isVisuallySelected as isVisuallySelectedFor, toggleDestination } from "../helpers/destinationSelection.js";
import { onActivateKeyDown, FOCUS_RING } from "../helpers/a11y.js";

// Small map pin shown next to countries, regions and resorts (the designer's pin icon).
const pinIcon = <PinIcon className="size-4" />;
// Tick rendered inside the checkbox; transparent (box reads as empty) until
// the row is selected.
const tickIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
// Checkbox shown on every row - an empty box when unselected, a filled tick
// when selected (matching the airport picker).
// (the row itself is the focusable checkbox, so the box is presentational)
const Checkbox = ({ checked }) => (
    <input type="checkbox" className="oly-form-check shrink-0 pointer-events-none" checked={checked} readOnly tabIndex={-1} aria-hidden="true" />
);

const DetailedSelectItem = ({ destination, onSelect, selected }) => {
    const [imgError, setImgError] = useState(false);
    const showImage = destination.type == "Hotel" && destination.image && !imgError;
    return (
        <div role="checkbox" tabIndex={0} aria-checked={selected} aria-label={destination.name}
            onKeyDown={onActivateKeyDown(() => onSelect(destination))}
            className={"flex items-center gap-3 rounded-[0.5rem] mr-2 min-h-20 p-2 cursor-pointer " + FOCUS_RING + " " + (selected ? "bg-ocean-100 text-primary" : "text-content_2 hover:bg-content_5")} onClick={() => onSelect(destination)}>
            <Checkbox checked={selected} />
            {/* Fixed-size image box so every row lines up regardless of name
                length; falls back to a pin when the image is missing/broken. */}
            <div className="w-16 h-16 shrink-0 rounded-[0.5rem] overflow-hidden bg-content_5 flex items-center justify-center text-content_3">
                {showImage
                    ? <img className="w-full h-full object-cover" src={destination.image + "?width=100"} onError={() => setImgError(true)} alt="" />
                    : pinIcon}
            </div>
            <div className="flex flex-col min-w-0 justify-center flex-1">
                <div className="text-tiny text-content_3 truncate">{destination.ancestors?.join(" > ")}</div>
                <div className="text-base leading-tight line-clamp-2">{destination.name}</div>
            </div>
        </div>);
}

const SimpleSelectItem = ({ destination, onSelect, type, selected }) => {
    const indent = type == "resort" ? "pl-10" : type == "region" ? "pl-5" : "";
    return (
        <div role="checkbox" tabIndex={0} aria-checked={selected} aria-label={destination.name}
            onKeyDown={onActivateKeyDown(() => onSelect(destination))}
            className={"flex items-center gap-2 rounded-[0.5rem] mr-2 form-checkbox-text-left cursor-pointer " + FOCUS_RING + " " + indent + (selected ? " bg-ocean-100 text-primary" : " text-content_2 hover:bg-content_5")} onClick={() => onSelect(destination)}>
            <Checkbox checked={selected} />
            <span className="shrink-0 text-content_3">{pinIcon}</span>
            <span className={"flex-1 " + (type == "country" ? "font-semibold text-primary" : "")}>{destination.name}</span>
        </div>)
}

// One-tap suggestions at the top of the panel (country codes as the search API uses them).
const POPULAR_COUNTRIES = ["GR", "CY"];

const DestinationPicker = ({ departureAirports, selectedDestinations = [], accomIsLocked, onUpdate, error }) => {
    const [flatDestinations, setFlatDestinations] = useState(null);
    const [filteredDestinations, setFilteredDestinations] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [filterText, setFilterText] = useState("");
    const dataSource = useContext(DataSourceContext);
    const isMobile = useContext(IsMobileContext);

    const isVisuallySelected = (code) => isVisuallySelectedFor(selectedDestinations, code);

    useEffect(() => {
        async function fetchData() {
            // Don't blank the list while re-fetching (e.g. when switching search
            // type) - keeping the previous data avoids the field flashing
            // "Loading..." on every tab switch. It's null only on first load.
            if (departureAirports.length > 0) {
                var destinations = await dataSource.getDestinations(departureAirports);
                setFlatDestinations(destinations);
                // Prune any selected destinations that aren't available for the
                // current airports / search type. Hotels (numeric codes) only
                // come from text search and aren't in the browse tree, so keep
                // those.
                if (selectedDestinations.length > 0 && !accomIsLocked) {
                    const valid = new Set();
                    for (const country of destinations.countries) {
                        valid.add(country.code);
                        for (const region of (country.regions || [])) {
                            valid.add(region.code);
                            for (const resort of (region.resorts || [])) {
                                valid.add(resort.code);
                            }
                        }
                    }
                    const kept = selectedDestinations.filter((d) => valid.has(d.code) || isHotelCode(d.code));
                    if (kept.length != selectedDestinations.length) {
                        onUpdate(kept);
                    }
                }
            }
        }
        fetchData();
    }, [departureAirports]);

    useEffect(() => {
        if (!filterText) {
            setFilteredDestinations(null);
            return;
        }
        // Debounce: wait for a pause in typing before hitting the search API, so
        // "greece" is one request rather than six. `cancelled` also drops a
        // stale in-flight response if the query changed again before it landed.
        let cancelled = false;
        setFilteredDestinations(null);
        const handle = setTimeout(async () => {
            const destinations = await dataSource.getDestinationsWithTextSearch(departureAirports, filterText);
            if (!cancelled) setFilteredDestinations(destinations);
        }, 250);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [filterText]);

    // Clear the search text on close so the field shows the selection summary
    // (placeholder), not leftover typed text.
    useEffect(() => {
        if (!isOpen) {
            setFilterText("");
        }
    }, [isOpen]);

    // Toggle a destination in/out of the selection (tree cascade / hotel rules
    // live in helpers/destinationSelection.js). The popover stays open so
    // several can be picked in one go.
    const onSelect = (selected) => onUpdate(toggleDestination(flatDestinations, selectedDestinations, selected));


    let inputText = "Choose Any";
    if (selectedDestinations.length == 1) {
        inputText = selectedDestinations[0].name;
    } else if (selectedDestinations.length > 1) {
        inputText = `${selectedDestinations.length} destinations`;
    }

    let entries = [];
    if (filteredDestinations) {
        // Render in the order the server returns (geography first, then hotels &
        // tours) and drop in a separator at the geography -> hotels boundary.
        const allResults = filteredDestinations.destinations || [];
        let geoSeen = false;
        let separatorAdded = false;
        allResults.forEach((d, i) => {
            const isHotel = d.type == "Hotel";
            if (!isHotel) geoSeen = true;
            if (isHotel && geoSeen && !separatorAdded) {
                entries.push(
                    <div key="hotels-separator" className="flex items-center gap-2 px-2 pt-3 pb-1">
                        <span className="flex-1 border-t border-content_4"></span>
                        <span className="text-xs font-semibold uppercase tracking-wide text-content_3">Hotels &amp; Tours</span>
                        <span className="flex-1 border-t border-content_4"></span>
                    </div>
                );
                separatorAdded = true;
            }
            entries.push(<DetailedSelectItem destination={d} key={i} onSelect={onSelect} selected={isVisuallySelected(d.code)} />);
        });
    } else {
        if (flatDestinations) {
            for (let index = 0; index < flatDestinations.countries.length; index++) {
                const country = flatDestinations.countries[index];
                entries.push(<SimpleSelectItem destination={country} key={index} onSelect={onSelect} type="country" selected={isVisuallySelected(country.code)} />);
                for (let rIndex = 0; rIndex < country.regions.length; rIndex++) {
                    const element = country.regions[rIndex];
                    // A catch-all "<country> all resorts" region isn't a real
                    // location - skip its row and surface the resorts directly
                    // under the country instead.
                    const isAllResorts = element.name && element.name.toLowerCase().trim().endsWith("all resorts");
                    if (!isAllResorts) {
                        entries.push(<SimpleSelectItem destination={element} key={index + "_" + rIndex} onSelect={onSelect} type="region" selected={isVisuallySelected(element.code)} />)
                    }
                    // Resorts ("locations") nested under each region; promoted to
                    // the region indent when the wrapper region is skipped.
                    const resorts = element.resorts || [];
                    for (let sIndex = 0; sIndex < resorts.length; sIndex++) {
                        const resort = resorts[sIndex];
                        entries.push(<SimpleSelectItem destination={resort} key={index + "_" + rIndex + "_" + sIndex} onSelect={onSelect} type={isAllResorts ? "region" : "resort"} selected={isVisuallySelected(resort.code)} />)
                    }
                }
            }
        }
    }

    // Popular one-tap picks, shown as pills when that country is available from the chosen airports.
    const popular = POPULAR_COUNTRIES
        .map((code) => flatDestinations?.countries?.find((c) => c.code == code))
        .filter((c) => c && !isVisuallySelected(c.code));

    // Header: the mobile search box, the popular pills, and what is selected as a short line - the first two
    // names and how many more ("Durres, Ksamil + 3 more"; the full list on hover). Picks are removed by unticking
    // them in the list, or with Clear Selection.
    const selectedNames = selectedDestinations.map((d) => d.name).join(", ");
    // the first two by name, then a count - nothing hidden behind an ellipsis
    const selectedSummary = selectedDestinations.slice(0, 2).map((d) => d.name).join(", ")
        + (selectedDestinations.length > 2 ? ` + ${selectedDestinations.length - 2} more` : "");
    let header = (
        <div className="space-y-2">
            {isMobile && (
                <input type="text" autoComplete="off" value={filterText} onChange={(e) => setFilterText(e.target.value)}
                    className="oly-form-input" placeholder="Search destinations or hotels" aria-label="Search destinations or hotels" />
            )}
            {popular.length > 0 && (
                <div className="flex items-center gap-1.5 text-sm">
                    <span className="shrink-0 text-ink-soft mr-0.5">Popular:</span>
                    {popular.map((c) => (
                        <button key={c.code} type="button" onClick={() => onSelect(c)}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-sky-100 px-3 py-1 text-navy-700 font-medium hover:border-sky-600 hover:bg-sky-50 cursor-pointer">
                            <span aria-hidden="true" className="text-sky-600">+</span>{c.name}
                        </button>
                    ))}
                </div>
            )}
            {selectedDestinations.length > 0 && (
                <p className="line-clamp-2 text-sm text-ink-soft" title={selectedNames}>
                    Selected: <span className="font-medium text-navy-700">{selectedSummary}</span>
                </p>
            )}
        </div>
    );

    // A "Done" button to close once selections are made (clicks toggle rather
    // than close, so we need an explicit way out).
    const footer = <PickerFooter onClear={() => onUpdate([])} onConfirm={() => setIsOpen(false)} clearDisabled={selectedDestinations.length == 0} />;

    // Empty-state: distinguish "still loading a search" from genuinely no
    // results / no destinations available (e.g. an index is unavailable).
    let emptyState = null;
    if (entries.length == 0) {
        if (filterText && filteredDestinations == null) {
            emptyState = null; // search in flight
        } else if (filterText) {
            emptyState = <div className="text-content_2 text-sm p-3 text-center">No matches for &ldquo;{filterText}&rdquo;.</div>;
        } else {
            emptyState = <div className="text-content_2 text-sm p-3 text-center">No destinations available.</div>;
        }
    }

    return <PopoverInput label="Destination" error={error} header={header} footer={footer} panelWidthClass={isMobile ? "" : "w-[25rem]"} disabled={departureAirports.length == 0 || accomIsLocked} icon={icon} inputText={inputText} isLoading={flatDestinations == null} onTextUpdate={setFilterText} filterText={filterText} isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="">
            {entries.length > 0 ? entries : emptyState}
        </div>
    </PopoverInput>
}
export default DestinationPicker;
